import { useState } from "react";
import { CheckCircle, Loader2 } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const serviceOptions = [
  "Screen Printing", "DTF Printing", "Embroidery", "Custom Jerseys",
  "Specialty Printing", "Graphic Design", "Not Sure — Help Me Decide",
];
const quantityOptions = [
  "1-11 pieces", "12-23 pieces", "24-47 pieces", "48-71 pieces",
  "72-143 pieces", "144-287 pieces", "288+ pieces",
];

export default function HubSpotForm() {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", company: "",
    service_type: "", quantity: "", message: "",
  });
  const [status, setStatus] = useState("idle");
  const [submittedName, setSubmittedName] = useState("");

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.service_type || !form.quantity) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/api/leads`, {
        ...form,
        needed_by: "",
        design_status: "Not specified",
        referral_source: "HubSpot Form",
      });
      setSubmittedName(form.full_name.split(" ")[0]);
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div data-testid="hubspot-form-success" className="text-center py-12">
        <div className="w-16 h-16 bg-green-900/30 border border-green-600/30 flex items-center justify-center mx-auto mb-5 rounded-full">
          <CheckCircle size={32} className="text-green-400" />
        </div>
        <h3 className="font-teko text-3xl text-white uppercase mb-2">Thanks, {submittedName}!</h3>
        <p className="text-neutral-400 font-inter text-sm mb-1">Your quote request has been submitted.</p>
        <p className="text-neutral-500 font-inter text-xs">We'll respond within 2 hours during business hours.</p>
        <button
          onClick={() => { setStatus("idle"); setForm({ full_name: "", email: "", phone: "", company: "", service_type: "", quantity: "", message: "" }); }}
          className="mt-6 text-sm text-red-500 hover:text-red-400 font-inter underline"
        >
          Submit another request
        </button>
      </div>
    );
  }

  return (
    <div data-testid="hubspot-form">
      {/* HubSpot Header */}
      <div className="flex items-center gap-2 mb-6">
        <svg viewBox="0 0 24 24" className="w-5 h-5 text-red-500 fill-current" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.62 9.29V6.4a1.78 1.78 0 0 0 1.03-1.6V4.73a1.78 1.78 0 0 0-1.78-1.78h-.07a1.78 1.78 0 0 0-1.78 1.78v.07c0 .69.4 1.29.97 1.58v2.91a5.07 5.07 0 0 0-2.46 1.23l-6.57-5.11a2.4 2.4 0 0 0 .07-.53 2.38 2.38 0 1 0-2.38 2.38c.42 0 .81-.12 1.15-.31l6.44 5.01a5.07 5.07 0 0 0-.52 2.23c0 .82.2 1.59.54 2.28l-2.02 2.02a1.7 1.7 0 0 0-.5-.08 1.72 1.72 0 1 0 1.72 1.72c0-.17-.03-.34-.08-.5l1.99-1.99A5.1 5.1 0 1 0 17.62 9.29zM16.87 17a2.73 2.73 0 1 1 0-5.45 2.73 2.73 0 0 1 0 5.45z"/>
        </svg>
        <span className="text-xs text-neutral-500 uppercase tracking-widest font-inter">Request a Quote</span>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name + Email row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-neutral-400 font-inter block mb-1.5">Full Name <span className="text-red-500">*</span></label>
            <input
              data-testid="hs-name"
              type="text"
              value={form.full_name}
              onChange={set("full_name")}
              placeholder="John Smith"
              required
              className="w-full bg-[#1a1a1a] border border-neutral-700 focus:border-red-500 focus:ring-1 focus:ring-red-500/20 text-white text-sm font-inter px-3.5 py-2.5 outline-none transition-colors placeholder:text-neutral-600"
            />
          </div>
          <div>
            <label className="text-xs text-neutral-400 font-inter block mb-1.5">Email <span className="text-red-500">*</span></label>
            <input
              data-testid="hs-email"
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder="john@company.com"
              required
              className="w-full bg-[#1a1a1a] border border-neutral-700 focus:border-red-500 focus:ring-1 focus:ring-red-500/20 text-white text-sm font-inter px-3.5 py-2.5 outline-none transition-colors placeholder:text-neutral-600"
            />
          </div>
        </div>

        {/* Phone + Company row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-neutral-400 font-inter block mb-1.5">Phone <span className="text-red-500">*</span></label>
            <input
              data-testid="hs-phone"
              type="tel"
              value={form.phone}
              onChange={set("phone")}
              placeholder="(214) 555-0000"
              required
              className="w-full bg-[#1a1a1a] border border-neutral-700 focus:border-red-500 focus:ring-1 focus:ring-red-500/20 text-white text-sm font-inter px-3.5 py-2.5 outline-none transition-colors placeholder:text-neutral-600"
            />
          </div>
          <div>
            <label className="text-xs text-neutral-400 font-inter block mb-1.5">Company</label>
            <input
              data-testid="hs-company"
              type="text"
              value={form.company}
              onChange={set("company")}
              placeholder="Optional"
              className="w-full bg-[#1a1a1a] border border-neutral-700 focus:border-red-500 focus:ring-1 focus:ring-red-500/20 text-white text-sm font-inter px-3.5 py-2.5 outline-none transition-colors placeholder:text-neutral-600"
            />
          </div>
        </div>

        {/* Service + Quantity row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-neutral-400 font-inter block mb-1.5">Service Needed <span className="text-red-500">*</span></label>
            <select
              data-testid="hs-service"
              value={form.service_type}
              onChange={set("service_type")}
              required
              className="w-full bg-[#1a1a1a] border border-neutral-700 focus:border-red-500 focus:ring-1 focus:ring-red-500/20 text-white text-sm font-inter px-3.5 py-2.5 outline-none transition-colors"
            >
              <option value="">Select a service</option>
              {serviceOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-neutral-400 font-inter block mb-1.5">Quantity <span className="text-red-500">*</span></label>
            <select
              data-testid="hs-quantity"
              value={form.quantity}
              onChange={set("quantity")}
              required
              className="w-full bg-[#1a1a1a] border border-neutral-700 focus:border-red-500 focus:ring-1 focus:ring-red-500/20 text-white text-sm font-inter px-3.5 py-2.5 outline-none transition-colors"
            >
              <option value="">Select quantity</option>
              {quantityOptions.map(q => <option key={q} value={q}>{q}</option>)}
            </select>
          </div>
        </div>

        {/* Message */}
        <div>
          <label className="text-xs text-neutral-400 font-inter block mb-1.5">Tell us about your project</label>
          <textarea
            data-testid="hs-message"
            value={form.message}
            onChange={set("message")}
            rows={3}
            placeholder="Describe your project: garment type, number of colors, design details, deadline..."
            className="w-full bg-[#1a1a1a] border border-neutral-700 focus:border-red-500 focus:ring-1 focus:ring-red-500/20 text-white text-sm font-inter px-3.5 py-2.5 outline-none transition-colors placeholder:text-neutral-600 resize-none"
          />
        </div>

        {status === "error" && (
          <div className="text-red-400 text-xs font-inter bg-red-900/20 border border-red-900/40 px-3 py-2" data-testid="hs-error">
            Something went wrong. Please try again or call (214) 555-0190.
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={status === "loading"}
          data-testid="hs-submit"
          className="w-full bg-red-600 hover:bg-red-700 text-white font-teko text-lg uppercase tracking-widest py-3.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {status === "loading" ? (
            <><Loader2 size={16} className="animate-spin" /> Submitting...</>
          ) : (
            "Get My Free Quote"
          )}
        </button>

        {/* HubSpot Branding */}
        <div className="flex items-center justify-center gap-2 pt-1">
          <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-neutral-600 fill-current" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.62 9.29V6.4a1.78 1.78 0 0 0 1.03-1.6V4.73a1.78 1.78 0 0 0-1.78-1.78h-.07a1.78 1.78 0 0 0-1.78 1.78v.07c0 .69.4 1.29.97 1.58v2.91a5.07 5.07 0 0 0-2.46 1.23l-6.57-5.11a2.4 2.4 0 0 0 .07-.53 2.38 2.38 0 1 0-2.38 2.38c.42 0 .81-.12 1.15-.31l6.44 5.01a5.07 5.07 0 0 0-.52 2.23c0 .82.2 1.59.54 2.28l-2.02 2.02a1.7 1.7 0 0 0-.5-.08 1.72 1.72 0 1 0 1.72 1.72c0-.17-.03-.34-.08-.5l1.99-1.99A5.1 5.1 0 1 0 17.62 9.29zM16.87 17a2.73 2.73 0 1 1 0-5.45 2.73 2.73 0 0 1 0 5.45z"/>
          </svg>
          <span className="text-[10px] text-neutral-600 font-inter">Powered by HubSpot</span>
        </div>

        <p className="text-center text-[10px] text-neutral-600 font-inter">
          We respond within 2 hours during business hours. Free quotes, no obligation.
        </p>
      </form>
    </div>
  );
}
